import { useEffect, useState } from "react";
import apiClient from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Pencil, Trash2, Plus } from "lucide-react";
import { toast } from "sonner";

const CATS = [
  "Skilled visa updates", "Student visa updates", "PR pathways",
  "Employer sponsorship", "Partner visa tips", "Visitor visa guidance",
  "Refusal and ART updates", "Invitation round updates",
];

const empty = { title: "", slug: "", excerpt: "", content: "", category: CATS[0], cover_image: "", author: "Shamima Yasmin Anwar", published: true };

export default function BlogManager() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [submitting, setSubmitting] = useState(false);

  const load = () => {
    setLoading(true);
    apiClient.get("/blog", { params: { published_only: false } })
      .then((r) => setPosts(r.data))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const openNew = () => { setEditing(null); setForm(empty); setOpen(true); };
  const openEdit = (p) => { setEditing(p); setForm({ ...empty, ...p }); setOpen(true); };

  const slugify = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");

  const onTitleChange = (v) => {
    update("title", v);
    if (!editing) update("slug", slugify(v));
  };

  const save = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const payload = { ...form, slug: form.slug || slugify(form.title) };
      if (editing) {
        await apiClient.put(`/admin/blog/${editing.id}`, payload);
        toast.success("Post updated");
      } else {
        await apiClient.post("/admin/blog", payload);
        toast.success("Post created");
      }
      setOpen(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSubmitting(false);
    }
  };

  const remove = async (post) => {
    if (!window.confirm(`Delete "${post.title}"?`)) return;
    try {
      await apiClient.delete(`/admin/blog/${post.id}`);
      toast.success("Post deleted");
      load();
    } catch (e) {
      toast.error("Delete failed");
    }
  };

  return (
    <div className="p-8">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="font-display text-3xl text-[#29324A]">Blog Manager</h1>
          <p className="text-sm text-slate-500 mt-1">{posts.length} post{posts.length === 1 ? "" : "s"}</p>
        </div>
        <Button data-testid="new-post-btn" onClick={openNew} className="bg-[#C43A89] hover:bg-[#a22b6e] rounded-none">
          <Plus className="h-4 w-4 mr-2" /> New Post
        </Button>
      </div>

      <div className="bg-white border border-slate-200 divide-y divide-slate-200">
        {loading ? (
          <div className="p-10 text-center text-slate-500">Loading...</div>
        ) : posts.length === 0 ? (
          <div className="p-10 text-center text-slate-500">No posts yet. Click "New Post" to create your first article.</div>
        ) : (
          posts.map((p) => (
            <div key={p.id} data-testid={`blog-row-${p.slug}`} className="p-5 flex items-center justify-between gap-4">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-3 mb-1">
                  <span className="text-[10px] font-bold tracking-widest text-[#C43A89] uppercase">{p.category}</span>
                  {!p.published && <span className="text-[10px] font-bold tracking-widest text-slate-400 uppercase">Draft</span>}
                </div>
                <h3 className="font-display text-lg text-[#29324A] truncate">{p.title}</h3>
                <p className="text-xs text-slate-500 mt-1 truncate">/{p.slug}</p>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <button data-testid={`edit-${p.slug}`} onClick={() => openEdit(p)} className="p-2 text-slate-500 hover:text-[#29324A]"><Pencil className="h-4 w-4" /></button>
                <button data-testid={`delete-${p.slug}`} onClick={() => remove(p)} className="p-2 text-slate-500 hover:text-red-500"><Trash2 className="h-4 w-4" /></button>
              </div>
            </div>
          ))
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">{editing ? "Edit Post" : "New Post"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={save} className="space-y-4">
            <div>
              <Label className="text-sm font-semibold mb-1.5 block">Title</Label>
              <Input data-testid="post-title" required value={form.title} onChange={(e) => onTitleChange(e.target.value)} />
            </div>
            <div>
              <Label className="text-sm font-semibold mb-1.5 block">Slug</Label>
              <Input data-testid="post-slug" required value={form.slug} onChange={(e) => update("slug", e.target.value)} />
            </div>
            <div>
              <Label className="text-sm font-semibold mb-1.5 block">Category</Label>
              <Select value={form.category} onValueChange={(v) => update("category", v)}>
                <SelectTrigger data-testid="post-category"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {CATS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-sm font-semibold mb-1.5 block">Cover image URL</Label>
              <Input data-testid="post-cover" value={form.cover_image} onChange={(e) => update("cover_image", e.target.value)} placeholder="https://..." />
            </div>
            <div>
              <Label className="text-sm font-semibold mb-1.5 block">Excerpt</Label>
              <Textarea data-testid="post-excerpt" required rows={2} value={form.excerpt} onChange={(e) => update("excerpt", e.target.value)} />
            </div>
            <div>
              <Label className="text-sm font-semibold mb-1.5 block">Content (HTML supported)</Label>
              <Textarea data-testid="post-content" required rows={10} value={form.content} onChange={(e) => update("content", e.target.value)} placeholder="<p>Your content here...</p>" className="font-mono text-xs" />
            </div>
            <div className="flex items-center gap-3">
              <Switch data-testid="post-published" checked={form.published} onCheckedChange={(v) => update("published", v)} />
              <Label className="text-sm font-semibold">Published</Label>
            </div>
            <Button data-testid="save-post-btn" type="submit" disabled={submitting} className="w-full bg-[#29324A] hover:bg-[#1f2638] rounded-none py-5 disabled:opacity-50">
              {submitting ? "Saving..." : (editing ? "Update Post" : "Create Post")}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
